import Phaser from 'phaser';
import type { ProjectileState } from '../core/projectile';
import type { TowerType } from '../core/types';
import { cycleFrames } from './coin';

/** Pinned projectile display size (px). */
const PROJ_DISPLAY = 16;
/** King Hambi's shards are "medium sized" — bigger than a bullet, smaller than a bomb. */
const SHARD_DISPLAY = 26;
/** Leafstorm Hambi's tornado of leaves is big enough to read as a whirlwind. */
const TORNADO_DISPLAY = 40;
/** Samurai Slicer's thrown daggers are SMALL, as the brief asks. */
const THROWN_DAGGER_DISPLAY = 20;
/** Bombs read bigger than pellets; the final-tier mega bomb bigger still. */
const BOMB_DISPLAY = 26;
const BOMB_DISPLAY_MEGA = 40;
/** The wizard's charged green orb reads bigger than its regular purple bolt. */
const SPECIAL_DISPLAY = 26;
/** Lightning Hambi's storm cloud rolls up the track fat enough to read as weather. */
const LIGHTNING_DISPLAY = 56;
/** Bow Hambi's arrows: long enough to read as arrows, and never tumbling. */
const ARROW_DISPLAY = 30;
/** Galactic Capy's fireballs; the black one reads heavier than the purple. */
const FIREBALL_DISPLAY = 24;
const FIREBALL_DISPLAY_BLACK = 36;
/**
 * General Capy's runners are drawn with the minigame's OWN frames, at its own
 * cadence — the capybara you tap on the EVENTS tab is the capybara that runs
 * up your track. They face RIGHT, so they mirror when they are heading left.
 */
const CAPY_DISPLAY = 52;
/** The Mayo Masher's sludge — a fat glob, a touch bigger than a plain shot. */
const MAYO_DISPLAY = 24;
/** Ultra Hammy's shots: the blue fireball, the dynamite and the purple crystal. */
const ULTRA_DISPLAY = 28;
/** The Dark Shop's three (spec 2026-10-12): poison ball, spinning skull, flaming skull. */
const HORROR_DISPLAY = 22;
const DEMONIC_DISPLAY = 32;
const HAMREAPER_DISPLAY = 30;
/** Angelic Hambi's yellow-white fireball (spec 2026-10-13). */
const ANGELIC_DISPLAY = 28;
const CAPY_RUN_MS = 110;
const CAPY_FRAMES: Record<'brown' | 'red', readonly string[]> = {
  brown: ['capy-run-1', 'capy-run-2'],
  red: ['capy-red-1', 'capy-red-2'],
};
/**
 * Mage Capy's laser. The shot is an ordinary projectile in the sim — this is
 * purely how it is drawn: a straight line from the staff to where the shot was
 * aimed, fading out over `BEAM_FADE_MS`. The colour follows the gem on his
 * staff, which the art turns from red to green at tier 3.
 */
const BEAM_FADE_MS = 180;
const BEAM_WIDTH = 4;
const BEAM_COLORS: Record<'low' | 'high', number> = { low: 0xe03a3a, high: 0x3ae06a };
/** Below this much horizontal movement in a frame, keep the current facing. */
const FLIP_DEADZONE = 0.5;
/** Bacon lobs on a cosmetic parabola; this is its peak height in px. */
const ARC_HEIGHT = 28;

/**
 * ProjectileView — binds a Phaser sprite to one ProjectileState by id.
 * Bacon ('aoe') is drawn on a cosmetic arc between from→to; the gameplay
 * position stays linear (the sim is authoritative).
 *
 * Depth: 3.5 (above towers at 3, below enemies at 4).
 */
export class ProjectileView {
  readonly id: number;
  readonly towerType: TowerType;
  readonly tier: 1 | 2 | 3 | 4;
  /** Last drawn world position — GameScene reads this to place impact decals. */
  lastPos = { x: 0, y: 0 };

  private readonly sprite: Phaser.GameObjects.Sprite;
  private readonly streak: Phaser.GameObjects.Sprite;
  private prev = { x: 0, y: 0 };
  private readonly arc: boolean;
  /**
   * How the sprite is held in flight. Most shots `tumble`; an arrow and a
   * fireball point `along-shot`; a running capybara stays `upright` and only
   * mirrors, because a capybara rotated to face along the track reads as a
   * capybara falling over.
   */
  private readonly orient: 'tumble' | 'along-shot' | 'upright';

  constructor(
    private readonly scene: Phaser.Scene,
    state: ProjectileState,
  ) {
    this.id = state.id;
    this.towerType = state.towerType;
    this.tier = state.tier;
    this.arc = state.behavior === 'aoe';
    this.orient =
      state.towerType === 'generalcapy'
        ? 'upright'
        : state.towerType === 'bow' ||
            state.towerType === 'galacticcapy' ||
            // The Ham Reaper's skull streams its flames behind it, and so
            // does Angelic Hambi's fireball.
            state.towerType === 'hamreaper' ||
            state.towerType === 'angelic'
          ? 'along-shot'
          : 'tumble';

    const display =
      state.towerType === 'generalcapy'
        ? CAPY_DISPLAY
        : state.towerType === 'galacticcapy'
          ? state.special
            ? FIREBALL_DISPLAY_BLACK
            : FIREBALL_DISPLAY
          : state.special
            ? SPECIAL_DISPLAY
            : state.towerType === 'lightning'
              ? LIGHTNING_DISPLAY
              : state.towerType === 'bow'
                ? ARROW_DISPLAY
                : state.towerType === 'kinghambi'
                  ? SHARD_DISPLAY
                  : state.towerType === 'leafstorm'
                    ? TORNADO_DISPLAY
                    : state.towerType === 'samurai'
                      ? THROWN_DAGGER_DISPLAY
                      : state.towerType === 'masher'
                        ? MAYO_DISPLAY
                        : state.towerType === 'ultrahammy'
                          ? ULTRA_DISPLAY
                          : state.towerType === 'horror'
                            ? HORROR_DISPLAY
                            : state.towerType === 'demonic'
                              ? DEMONIC_DISPLAY
                              : state.towerType === 'hamreaper'
                                ? HAMREAPER_DISPLAY
                                : state.towerType === 'angelic'
                                  ? ANGELIC_DISPLAY
                                  : state.towerType === 'bomb'
                                    ? state.tier >= 3 // tiers 3 and 4 (Evolution) both throw the mega bomb
                                      ? BOMB_DISPLAY_MEGA
                                      : BOMB_DISPLAY
                                    : PROJ_DISPLAY;

    // The Fire Mage's 25% roll flies as a BLUE fireball. The colour is the only
    // cue the player gets while the shot is in the air, so it is bound to the
    // band the shot actually rolled — not re-drawn at impact.
    // Shrekbook Hambi fires the Hambi Tower's shot — the same art, by design, so
    // there is no proj-shrekbook key to load.
    // Mage Capy is drawn as a beam (below), so his projectile sprites are
    // created hidden and borrow a texture that exists rather than asking for a
    // `proj-magecapy` that was deliberately never drawn. Mini Gunner fires the
    // Gunner Hambi's bullet — the same art, by design, so there is no
    // proj-minigunner key either.
    const artType =
      state.towerType === 'shrekbook' || state.towerType === 'magecapy'
        ? 'patty'
        : state.towerType === 'minigunner'
          ? 'gunner'
          : state.towerType;
    // Bow Hambi's poison arrow is bound to its roll the same way: the green
    // sparkles ARE the tell that this one will poison rather than hit.
    // General Capy's red imposter is bound to its roll the same way the Fire
    // Mage's blue fireball is: the colour IS the warning, decided when the
    // runner set off, never re-decided on contact.
    const capyFrames = CAPY_FRAMES[state.redSpawn ? 'red' : 'brown'];
    const texKey =
      artType === 'generalcapy'
        ? capyFrames[0]
        : artType === 'galacticcapy'
          ? state.special
            ? 'proj-capyfire-black'
            : 'proj-capyfire'
          : state.special
            ? `proj-${artType}-green`
            : artType === 'firemage' && state.shotKind === 'crit'
              ? 'proj-firemage-blue'
              : // King Capy's blue gem is bound to its roll exactly as the Fire
                // Mage's blue fireball is: the colour IS the tell, decided when
                // the gem left his hand and never re-decided on contact.
                artType === 'kingcapy' && state.shotKind === 'crit'
                ? 'proj-kingcapy-blue'
                : artType === 'bow' && state.poison
                  ? 'proj-bow-poison'
                  : // Ultra Hammy's shot changes with his tier (spec
                    // 2026-09-29): the Fire Mage's blue fireball at tier 1,
                    // dynamite at 2–3 (his minions throw tier 2), a purple
                    // crystal at 4.
                    artType === 'ultrahammy'
                    ? state.tier === 1
                      ? 'proj-firemage-blue'
                      : state.tier === 4
                        ? 'proj-ultrahammy-4'
                        : 'proj-ultrahammy'
                    : `proj-${artType}`;

    this.sprite = scene.add
      .sprite(state.pos.x, state.pos.y, texKey)
      .setDepth(3.5)
      .setDisplaySize(display, display);

    this.streak = scene.add
      .sprite(state.pos.x, state.pos.y, texKey)
      .setDepth(3.4)
      .setDisplaySize(display, display)
      .setAlpha(state.towerType === 'pickle' ? 0.45 : 0.25); // pickle = longer/brighter streak
    this.prev = { x: state.pos.x, y: state.pos.y };
    if (this.orient === 'along-shot') {
      const angle = Math.atan2(state.to.y - state.from.y, state.to.x - state.from.x);
      this.sprite.setRotation(angle);
      this.streak.setRotation(angle);
      if (state.poison) this.streak.setTint(0x7ccd3a).setAlpha(0.5);
      // His skull art faces LEFT (flames trailing right); the rotation
      // assumes art that faces right. Angelic Hambi's fireball too.
      if (state.towerType === 'hamreaper' || state.towerType === 'angelic') {
        this.sprite.setFlipX(true);
        this.streak.setFlipX(true);
      }
    }
    if (this.orient === 'upright') {
      // No ghost trail behind a running animal — the gallop IS the motion cue.
      this.streak.setVisible(false);
      cycleFrames(this.scene, this.sprite, capyFrames, CAPY_RUN_MS);
    }

    // Mage Capy: hide the projectile entirely and flash a beam along its line.
    // The beam is its own object with its own fade, so it outlives this view's
    // ~0.1s flight and reads as a laser rather than a blink.
    if (state.towerType === 'magecapy') {
      this.sprite.setVisible(false);
      this.streak.setVisible(false);
      const colour = BEAM_COLORS[state.tier >= 3 ? 'high' : 'low'];
      const beam = scene.add.graphics().setDepth(3.5);
      beam.lineStyle(BEAM_WIDTH, colour, 1);
      beam.beginPath();
      beam.moveTo(state.from.x, state.from.y);
      beam.lineTo(state.to.x, state.to.y);
      beam.strokePath();
      scene.tweens.add({
        targets: beam,
        alpha: 0,
        duration: BEAM_FADE_MS,
        onComplete: () => beam.destroy(),
      });
    }
  }

  update(state: ProjectileState): void {
    const { x } = state.pos;
    let { y } = state.pos;

    if (this.arc) {
      // Cosmetic parabola: t = progress along from→to, peak at t=0.5.
      const total = Math.hypot(state.to.x - state.from.x, state.to.y - state.from.y) || 1;
      const done = Math.hypot(state.pos.x - state.from.x, state.pos.y - state.from.y);
      const t = Math.min(1, done / total);
      y -= ARC_HEIGHT * 4 * t * (1 - t); // 0 at ends, peak mid-flight
    }

    this.streak.setPosition(this.prev.x, this.prev.y);
    // Which way a runner is heading, measured from where it actually was —
    // the track curves, so the shot's overall from→to vector is not the
    // direction it is facing at this moment.
    const dx = x - this.prev.x;
    this.prev = { x, y };

    this.sprite.setPosition(x, y);
    // The tornado spins twice as fast as a tumbling shot — it IS the spin.
    if (this.orient === 'tumble')
      this.sprite.rotation += this.towerType === 'leafstorm' ? 0.6 : 0.3; // tumble/spin
    else if (this.orient === 'upright' && Math.abs(dx) > FLIP_DEADZONE) {
      this.sprite.setFlipX(dx < 0);
    }
    this.lastPos.x = x;
    this.lastPos.y = y;
  }

  destroy(): void {
    this.streak.destroy();
    this.sprite.destroy();
  }
}
